/**
 * @fileoverview Signature background removal (MOR-1033).
 *
 * Uploaded signature images are almost always a photo or scan of ink on
 * white paper. Rendered into a letter, that white rectangle overlays the
 * adjacent text (the signature sits close to the sign-off lines), so the
 * paper background must go: only the ink strokes should render.
 *
 * Pure-canvas implementation — no external service, so signatures never
 * leave the browser and the flow works offline. The approach:
 *
 *  1. Estimate the paper colour from the image border (median luminance of
 *     a 2%-thick frame). Signatures are drawn in the middle; the border is
 *     reliably background.
 *  2. If the border is already transparent (a processed PNG) or dark
 *     (not paper — e.g. a white-ink-on-black asset we shouldn't invert),
 *     leave the file untouched.
 *  3. Otherwise map each pixel's luminance to alpha on a ramp anchored at
 *     the estimated paper colour: paper → fully transparent, ink → fully
 *     opaque, the anti-aliased greys between → partial alpha, which keeps
 *     stroke edges smooth on any letter background.
 *
 * The output is re-encoded as PNG (the only web-safe format with alpha).
 */

/** Longest edge of the processed image; larger uploads are downscaled. */
const MAX_DIMENSION = 1600;

/**
 * Luminance ramp width (in 0–255 luminance units) between "definitely
 * paper" and "definitely ink". Wider = softer stroke edges.
 */
const ALPHA_RAMP = 90;

/**
 * How far below the estimated paper luminance the "definitely paper"
 * threshold sits. Absorbs paper texture, shadows and JPEG noise.
 */
const PAPER_MARGIN = 24;

/** Border luminance below which we assume the image is not ink-on-paper. */
const MIN_PAPER_LUMINANCE = 140;

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Median luminance of the image's border frame, or null when the border is
 * mostly transparent already (alpha < 16 for over half the samples).
 */
function estimatePaperLuminance(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): number | null {
  const frame = Math.max(1, Math.round(Math.min(width, height) * 0.02));
  const samples: number[] = [];
  let transparent = 0;
  const sample = (x: number, y: number) => {
    const i = (y * width + x) * 4;
    if (data[i + 3] < 16) {
      transparent++;
      return;
    }
    samples.push(luminance(data[i], data[i + 1], data[i + 2]));
  };
  for (let y = 0; y < height; y++) {
    const isEdgeRow = y < frame || y >= height - frame;
    if (isEdgeRow) {
      for (let x = 0; x < width; x++) sample(x, y);
    } else {
      for (let x = 0; x < frame; x++) sample(x, y);
      for (let x = width - frame; x < width; x++) sample(x, y);
    }
  }
  if (transparent > samples.length) return null;
  if (samples.length === 0) return null;
  samples.sort((a, b) => a - b);
  return samples[Math.floor(samples.length / 2)];
}

/**
 * Map paper pixels to transparent in place. Exported for tests — operates
 * on raw RGBA data so it runs under happy-dom without a real canvas.
 * Returns false when the image was left untouched (already transparent,
 * or not ink-on-light-paper).
 */
export function knockOutPaperBackground(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): boolean {
  const paper = estimatePaperLuminance(data, width, height);
  if (paper === null || paper < MIN_PAPER_LUMINANCE) return false;

  const paperFloor = paper - PAPER_MARGIN;
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (alpha === 0) continue;
    const l = luminance(data[i], data[i + 1], data[i + 2]);
    // 0 at paperFloor (and above), 1 a full ramp below it.
    const ink = Math.min(1, Math.max(0, (paperFloor - l) / ALPHA_RAMP));
    data[i + 3] = Math.round(alpha * ink);
  }
  return true;
}

/**
 * Remove the paper background from an uploaded signature image and return
 * a transparent PNG. Falls back to the original file when the image
 * decodes but isn't ink-on-light-paper, and when anything goes wrong —
 * an upload should never fail because the cleanup pass did.
 */
export async function removeSignatureBackground(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    try {
      const scale = Math.min(
        1,
        MAX_DIMENSION / Math.max(bitmap.width, bitmap.height),
      );
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      const changed = knockOutPaperBackground(imageData.data, width, height);
      if (!changed && scale === 1) return file;
      ctx.putImageData(imageData, 0, 0);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      if (!blob) return file;
      return new File([blob], "signature.png", { type: "image/png" });
    } finally {
      bitmap.close();
    }
  } catch {
    return file;
  }
}
